import { useEffect, useState } from "react";

import type {
  PrefillField,
  Template,
  TemplateAPIPDFResponse,
  TemplateAPIResponse,
} from "./types";
import useFetchContract from "./use-fetch-contract";

const templateURL = "contracts/template";

export default function useTemplate(id?: string) {
  const [template, setTemplate] = useState<Template | null>(null);

  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    isLoading: useFetchContractIsLoading,
    error: useFetchContractError,
    get,
    create,
    update,
    remove,
  } = useFetchContract();

  const getTemplate = async (id: string) => {
    const res = (await get(`contracts/template/${id}`)) as TemplateAPIResponse;

    return res.data;
  };

  const getTemplateFields = async (
    id: string,
    prefill?: PrefillField | any
  ): Promise<[string, any]> => {
    let url = new URL(
      process.env.REACT_APP_API_CONTRACT_URL + `${templateURL}/${id}/fields`
    );

    const { provider, company, person, deal, contact } = prefill || {};

    if (provider) {
      url.searchParams.set("provider", provider);
    }
    if (person) {
      url.searchParams.set("person", person);
    }
    if (company) {
      url.searchParams.set("organization", company);
    }
    if (deal) {
      url.searchParams.set("deal", deal);
    }
    if (contact) {
      url.searchParams.set("contact", contact);
    }

    const path =
      url.pathname.substring(url.pathname.indexOf(templateURL)) +
      "?" +
      url.searchParams;

    const res = (await get(path)) as any;
    return res.data;
  };

  const getTemplatePDFURL = async (id: string) => {
    return (await get(`${templateURL}/${id}/pdf`)) as TemplateAPIPDFResponse;
  };

  const createTemplate = async (template: Template) => {
    return await create(templateURL, template);
  };

  const updateTemplate = async (id: string, template: Template) => {
    return await update(`${templateURL}/${id}`, template);
  };

  const removeTemplate = async (id: string) => {
    return await remove(`${templateURL}/${id}`);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const template = (await get(
          `${templateURL}/${id}`
        )) as TemplateAPIResponse;
        setTemplate(template.data as any);
      } catch (e) {
        if (e instanceof Error) {
          setError(e);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (id && !template && !isLoading && !useFetchContractIsLoading && !error) {
      setIsLoading(true);
      fetch();
    }
  }, [id, template, isLoading, useFetchContractIsLoading, error, get]);

  return {
    template,
    isLoading: isLoading || useFetchContractIsLoading,
    error: error || useFetchContractError,
    getTemplate,
    getTemplatePDFURL,
    getTemplateFields,
    createTemplate,
    updateTemplate,
    removeTemplate,
  };
}
